"use client";

import { cn } from "@jesus-central-church/shared";
import React from "react";
import { Image } from "../media/image";

export type ImageCardTextColor =
  | "boldness"
  | "freedom"
  | "strategy-red"
  | "strategy-gold"
  | "strategy-green";

export interface ImageCardProperties {
  image?: {
    src: string;
    alt?: string;
  };
  title: string;
  description?: string;
  backgroundColor?: string;
  textColor?: ImageCardTextColor;
  actions?: React.ReactNode;
  className?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any; // For additional props like storyblokEditable
}

const getTextColorClasses = (
  color?: ImageCardTextColor,
): { text: string; border: string } => {
  const colorMap: Record<ImageCardTextColor, { text: string; border: string }> =
    {
      boldness: { text: "text-boldness", border: "border-boldness/10" },
      freedom: { text: "text-freedom", border: "border-freedom/10" },
      "strategy-red": {
        text: "text-strategy-red",
        border: "border-strategy-red/10",
      },
      "strategy-gold": {
        text: "text-strategy-gold",
        border: "border-strategy-gold/10",
      },
      "strategy-green": {
        text: "text-strategy-green",
        border: "border-strategy-green/10",
      },
    };
  return colorMap[color || "boldness"] || colorMap.boldness;
};

export function ImageCard({
  image,
  title,
  description,
  backgroundColor,
  textColor,
  actions,
  className,
  ...additionalProperties
}: ImageCardProperties) {
  const colorClasses = getTextColorClasses(textColor);
  const backgroundColorClass = backgroundColor ? `bg-${backgroundColor}` : "";

  return (
    <article
      className={cn(
        "group flex h-full flex-col overflow-hidden rounded-3xl border transition-all",
        colorClasses.border,
        colorClasses.text,
        backgroundColorClass,
        className,
      )}
      {...additionalProperties}
    >
      {image?.src && (
        <div className="bg-boldness/5 relative aspect-[16/10] w-full overflow-hidden">
          <Image
            src={image.src}
            alt={image.alt || title || "Card image"}
            fill
            className="object-cover transition-transform duration-500 group-hover:scale-105"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          />
        </div>
      )}
      <div className="flex flex-1 flex-col gap-3 p-6 sm:p-8">
        <h3 className="font-heading text-2xl tracking-wide uppercase sm:text-3xl">
          {title}
        </h3>
        {description && (
          <p
            className={cn(
              "font-body flex-1 text-base leading-relaxed",
              colorClasses.text.replace("text-", "text-") + "/85",
            )}
          >
            {description}
          </p>
        )}
        {actions && <div className="mt-3 flex flex-wrap gap-3 sm:mt-4">{actions}</div>}
      </div>
    </article>
  );
}
